import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useI18n } from '../contexts/I18nContext';
import LeendrLogoMark from '../components/LeendrLogoMark';
import {
  ArrowRight, ShieldCheck, Lock, GlobeHemisphereWest,
  InstagramLogo, TwitterLogo, LinkedinLogo,
  UserCirclePlus, Tag
} from '@phosphor-icons/react';
import axios from 'axios';
import { trackCta, useTrackView } from '../utils/tracker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const C = {
  primary: '#534AB7',
  dark: '#26215C',
  accent: '#AFA9EC',
  bg: '#EEEDFE',
  text: '#2C2C2A',
  white: '#FFFFFF',
  border: 'rgba(175,169,236,0.4)',
  borderLight: 'rgba(175,169,236,0.25)',
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] } }),
};

const T = {
  de: {
    nav: { how: 'Wie es funktioniert', categories: 'Kategorien', trust: 'Sicherheit', cta: 'Jetzt registrieren' },
    hero: {
      h1_1: 'Miete alles.', h1_2: 'Von allen.',
      subtitle: 'Der Marktplatz für Peer-to-Peer Vermietung — von Werkzeug bis Kamera, von Zelt bis Lastenrad.',
      btnRent: 'Etwas vermieten', btnBorrow: 'Etwas mieten',
    },
    how: {
      label: 'Wie es funktioniert',
      title: 'Zwei Perspektiven. Ein Marktplatz.',
      left: 'Als Vermieter', right: 'Als Mieter',
      vermieter: [
        { num: '1', title: 'Inserat erstellen', desc: 'Fotografiere deinen Gegenstand und stelle ihn in wenigen Minuten online.' },
        { num: '2', title: 'Anfrage annehmen', desc: 'Prüfe eingehende Mietanfragen und bestätige mit einem Klick.' },
        { num: '3', title: 'Geld verdienen', desc: 'Erhalte die Zahlung sicher über die Plattform nach erfolgreicher Übergabe.' },
      ],
      mieter: [
        { num: '1', title: 'Artikel suchen', desc: 'Finde, was du brauchst — in deiner Nähe, zum besten Preis.' },
        { num: '2', title: 'Buchen & bezahlen', desc: 'Wähle deinen Zeitraum und bezahle sicher über leendr.' },
        { num: '3', title: 'Abholen & zurückgeben', desc: 'Nutze den Gegenstand und gib ihn zum vereinbarten Zeitpunkt zurück.' },
      ],
    },
    categories: {
      label: 'Kategorien',
      title: 'Was auch immer du brauchst',
    },
    trust: {
      label: 'Sicherheit',
      title: 'Sicher. Einfach. Nachhaltig.',
      cards: [
        { icon: 'check', title: 'Verifizierte Nutzer', desc: 'ID-Verifizierung für alle Nutzer auf der Plattform.' },
        { icon: 'lock', title: 'Sichere Zahlung', desc: 'Dein Geld wird erst nach erfolgreicher Übergabe freigegeben.' },
        { icon: 'globe', title: 'Für die Umwelt', desc: 'Teilen statt Kaufen schont Ressourcen und spart CO\u2082.' },
      ],
    },
    city: {
      title: 'Leendr startet bald in Deutschland',
      subtitle: 'Sei einer der Ersten in deiner Stadt und sichere dir exklusiven Early-Access',
    },
    waitlist: {
      title: 'Sei dabei von Anfang an',
      placeholder: 'Deine E-Mail-Adresse',
      btnVermieter: 'Als Vermieter registrieren',
      btnMieter: 'Als Mieter registrieren',
      fine: 'Kostenlos \u00B7 Keine Kreditkarte \u00B7 Jederzeit kündbar',
      success: 'Du bist dabei! Wir melden uns bald.',
      exists: 'Diese E-Mail ist bereits registriert.',
    },
    footer: {
      desc: 'Der Marktplatz für Peer-to-Peer Vermietung. Miete alles, von allen, überall.',
      platform: 'Plattform', browse: 'Entdecken', signup: 'Registrieren', login: 'Anmelden', how: 'Wie es funktioniert',
      legal: 'Rechtliches', imprint: 'Impressum', privacy: 'Datenschutz', terms: 'AGB', contact: 'Kontakt',
      rights: 'Leendr GmbH',
    },
  },
  en: {
    nav: { how: 'How it works', categories: 'Categories', trust: 'Safety', cta: 'Sign up now' },
    hero: {
      h1_1: 'Rent anything.', h1_2: 'From everyone.',
      subtitle: 'The marketplace for peer-to-peer rentals — from tools to cameras, tents to cargo bikes.',
      btnRent: 'Rent something out', btnBorrow: 'Rent something',
    },
    how: {
      label: 'How it works',
      title: 'Two perspectives. One marketplace.',
      left: 'As a lender', right: 'As a renter',
      vermieter: [
        { num: '1', title: 'Create a listing', desc: 'Take a photo and list your item in minutes.' },
        { num: '2', title: 'Accept requests', desc: 'Review rental requests and confirm with one click.' },
        { num: '3', title: 'Earn money', desc: 'Receive payment securely through the platform after handover.' },
      ],
      mieter: [
        { num: '1', title: 'Search items', desc: 'Find what you need — nearby, at the best price.' },
        { num: '2', title: 'Book & pay', desc: 'Choose your dates and pay securely through leendr.' },
        { num: '3', title: 'Pick up & return', desc: 'Use the item and return it at the agreed time.' },
      ],
    },
    categories: { label: 'Categories', title: 'Whatever you need' },
    trust: {
      label: 'Safety',
      title: 'Safe. Simple. Sustainable.',
      cards: [
        { icon: 'check', title: 'Verified users', desc: 'ID verification for all users on the platform.' },
        { icon: 'lock', title: 'Secure payments', desc: 'Your money is only released after successful handover.' },
        { icon: 'globe', title: 'For the planet', desc: 'Sharing instead of buying saves resources and CO\u2082.' },
      ],
    },
    city: {
      title: 'Leendr is launching soon in Germany',
      subtitle: 'Be one of the first in your city and secure exclusive early access',
    },
    waitlist: {
      title: 'Be there from the start',
      placeholder: 'Your email address',
      btnVermieter: 'Register as lender',
      btnMieter: 'Register as renter',
      fine: 'Free \u00B7 No credit card \u00B7 Cancel anytime',
      success: 'You\'re in! We\'ll be in touch soon.',
      exists: 'This email is already registered.',
    },
    footer: {
      desc: 'The marketplace for peer-to-peer rentals. Rent anything, from everyone, everywhere.',
      platform: 'Platform', browse: 'Browse', signup: 'Sign up', login: 'Sign in', how: 'How it works',
      legal: 'Legal', imprint: 'Imprint', privacy: 'Privacy', terms: 'Terms', contact: 'Contact',
      rights: 'Leendr GmbH',
    },
  },
  fr: {
    nav: { how: 'Comment ça marche', categories: 'Catégories', trust: 'Sécurité', cta: 'S\'inscrire' },
    hero: {
      h1_1: 'Louez tout.', h1_2: 'À tout le monde.',
      subtitle: 'La marketplace de location entre particuliers — des outils aux caméras, des tentes aux vélos cargo.',
      btnRent: 'Louer un objet', btnBorrow: 'Trouver un objet',
    },
    how: {
      label: 'Comment ça marche', title: 'Deux perspectives. Un marketplace.',
      left: 'En tant que loueur', right: 'En tant que locataire',
      vermieter: [
        { num: '1', title: 'Créer une annonce', desc: 'Prenez une photo et publiez votre objet en quelques minutes.' },
        { num: '2', title: 'Accepter les demandes', desc: 'Examinez les demandes et confirmez en un clic.' },
        { num: '3', title: 'Gagner de l\'argent', desc: 'Recevez le paiement en toute sécurité via la plateforme.' },
      ],
      mieter: [
        { num: '1', title: 'Chercher des articles', desc: 'Trouvez ce dont vous avez besoin — près de chez vous.' },
        { num: '2', title: 'Réserver & payer', desc: 'Choisissez vos dates et payez en toute sécurité.' },
        { num: '3', title: 'Récupérer & retourner', desc: 'Utilisez l\'objet et retournez-le à l\'heure convenue.' },
      ],
    },
    categories: { label: 'Catégories', title: 'Tout ce dont vous avez besoin' },
    trust: {
      label: 'Sécurité', title: 'Sûr. Simple. Durable.',
      cards: [
        { icon: 'check', title: 'Utilisateurs vérifiés', desc: 'Vérification d\'identité pour tous les utilisateurs.' },
        { icon: 'lock', title: 'Paiement sécurisé', desc: 'Votre argent n\'est libéré qu\'après la remise réussie.' },
        { icon: 'globe', title: 'Pour la planète', desc: 'Partager au lieu d\'acheter économise des ressources et du CO\u2082.' },
      ],
    },
    city: { title: 'Leendr arrive bientôt en Allemagne', subtitle: 'Soyez parmi les premiers dans votre ville' },
    waitlist: {
      title: 'Soyez là dès le début', placeholder: 'Votre adresse e-mail',
      btnVermieter: 'S\'inscrire comme loueur', btnMieter: 'S\'inscrire comme locataire',
      fine: 'Gratuit \u00B7 Sans carte \u00B7 Annulable', success: 'Vous êtes inscrit !', exists: 'Cet e-mail est déjà enregistré.',
    },
    footer: {
      desc: 'La marketplace de location P2P. Louez tout, à tout le monde, partout.',
      platform: 'Plateforme', browse: 'Explorer', signup: 'S\'inscrire', login: 'Se connecter', how: 'Comment ça marche',
      legal: 'Mentions légales', imprint: 'Mentions légales', privacy: 'Confidentialité', terms: 'CGU', contact: 'Contact',
      rights: 'Leendr GmbH',
    },
  },
  es: {
    nav: { how: 'Cómo funciona', categories: 'Categorías', trust: 'Seguridad', cta: 'Registrarse' },
    hero: {
      h1_1: 'Alquila todo.', h1_2: 'De todos.',
      subtitle: 'El marketplace de alquiler entre particulares — de herramientas a cámaras, de tiendas a bicicletas de carga.',
      btnRent: 'Alquilar algo', btnBorrow: 'Buscar algo',
    },
    how: {
      label: 'Cómo funciona', title: 'Dos perspectivas. Un marketplace.',
      left: 'Como arrendador', right: 'Como arrendatario',
      vermieter: [
        { num: '1', title: 'Crear anuncio', desc: 'Toma una foto y publica tu artículo en minutos.' },
        { num: '2', title: 'Aceptar solicitudes', desc: 'Revisa las solicitudes y confirma con un clic.' },
        { num: '3', title: 'Ganar dinero', desc: 'Recibe el pago de forma segura a través de la plataforma.' },
      ],
      mieter: [
        { num: '1', title: 'Buscar artículos', desc: 'Encuentra lo que necesitas — cerca de ti, al mejor precio.' },
        { num: '2', title: 'Reservar y pagar', desc: 'Elige tus fechas y paga de forma segura.' },
        { num: '3', title: 'Recoger y devolver', desc: 'Usa el artículo y devuélvelo en el momento acordado.' },
      ],
    },
    categories: { label: 'Categorías', title: 'Lo que sea que necesites' },
    trust: {
      label: 'Seguridad', title: 'Seguro. Simple. Sostenible.',
      cards: [
        { icon: 'check', title: 'Usuarios verificados', desc: 'Verificación de identidad para todos los usuarios.' },
        { icon: 'lock', title: 'Pago seguro', desc: 'Tu dinero se libera solo después de la entrega exitosa.' },
        { icon: 'globe', title: 'Para el planeta', desc: 'Compartir en vez de comprar ahorra recursos y CO\u2082.' },
      ],
    },
    city: { title: 'Leendr llega pronto a Alemania', subtitle: 'Sé uno de los primeros en tu ciudad' },
    waitlist: {
      title: 'Sé parte desde el inicio', placeholder: 'Tu correo electrónico',
      btnVermieter: 'Registrarse como arrendador', btnMieter: 'Registrarse como arrendatario',
      fine: 'Gratis \u00B7 Sin tarjeta \u00B7 Cancelable', success: '\u00A1Estás dentro!', exists: 'Este correo ya está registrado.',
    },
    footer: {
      desc: 'El marketplace de alquiler P2P. Alquila todo, de todos, en todas partes.',
      platform: 'Plataforma', browse: 'Explorar', signup: 'Registrarse', login: 'Iniciar sesión', how: 'Cómo funciona',
      legal: 'Legal', imprint: 'Aviso legal', privacy: 'Privacidad', terms: 'Términos', contact: 'Contacto',
      rights: 'Leendr GmbH',
    },
  },
  it: {
    nav: { how: 'Come funziona', categories: 'Categorie', trust: 'Sicurezza', cta: 'Registrati' },
    hero: {
      h1_1: 'Noleggia tutto.', h1_2: 'Da chiunque.',
      subtitle: 'Il marketplace per il noleggio peer-to-peer — dagli attrezzi alle fotocamere, dalle tende alle cargo bike.',
      btnRent: 'Noleggiare qualcosa', btnBorrow: 'Cercare qualcosa',
    },
    how: {
      label: 'Come funziona', title: 'Due prospettive. Un marketplace.',
      left: 'Come noleggiatore', right: 'Come locatario',
      vermieter: [
        { num: '1', title: 'Crea un annuncio', desc: 'Scatta una foto e pubblica il tuo oggetto in pochi minuti.' },
        { num: '2', title: 'Accetta richieste', desc: 'Esamina le richieste e conferma con un clic.' },
        { num: '3', title: 'Guadagna', desc: 'Ricevi il pagamento in sicurezza tramite la piattaforma.' },
      ],
      mieter: [
        { num: '1', title: 'Cerca articoli', desc: 'Trova ciò che ti serve — vicino a te, al miglior prezzo.' },
        { num: '2', title: 'Prenota e paga', desc: 'Scegli le date e paga in sicurezza.' },
        { num: '3', title: 'Ritira e restituisci', desc: 'Usa l\'oggetto e restituiscilo al momento concordato.' },
      ],
    },
    categories: { label: 'Categorie', title: 'Qualsiasi cosa ti serva' },
    trust: {
      label: 'Sicurezza', title: 'Sicuro. Semplice. Sostenibile.',
      cards: [
        { icon: 'check', title: 'Utenti verificati', desc: 'Verifica dell\'identità per tutti gli utenti.' },
        { icon: 'lock', title: 'Pagamento sicuro', desc: 'Il tuo denaro viene rilasciato solo dopo la consegna riuscita.' },
        { icon: 'globe', title: 'Per il pianeta', desc: 'Condividere invece di comprare risparmia risorse e CO\u2082.' },
      ],
    },
    city: { title: 'Leendr arriva presto in Germania', subtitle: 'Sii tra i primi nella tua città' },
    waitlist: {
      title: 'Esserci dall\'inizio', placeholder: 'Il tuo indirizzo e-mail',
      btnVermieter: 'Registrati come noleggiatore', btnMieter: 'Registrati come locatario',
      fine: 'Gratuito \u00B7 Nessuna carta \u00B7 Cancellabile', success: 'Sei dentro!', exists: 'Questa email è già registrata.',
    },
    footer: {
      desc: 'Il marketplace di noleggio P2P. Noleggia tutto, da chiunque, ovunque.',
      platform: 'Piattaforma', browse: 'Esplora', signup: 'Registrati', login: 'Accedi', how: 'Come funziona',
      legal: 'Legale', imprint: 'Note legali', privacy: 'Privacy', terms: 'Termini', contact: 'Contatto',
      rights: 'Leendr GmbH',
    },
  },
  uk: {
    nav: { how: 'Як це працює', categories: 'Категорії', trust: 'Безпека', cta: 'Зареєструватися' },
    hero: {
      h1_1: 'Орендуй все.', h1_2: 'Від усіх.',
      subtitle: 'Маркетплейс для однорангової оренди — від інструментів до камер, від наметів до вантажних велосипедів.',
      btnRent: 'Здати в оренду', btnBorrow: 'Орендувати щось',
    },
    how: {
      label: 'Як це працює', title: 'Дві перспективи. Один маркетплейс.',
      left: 'Як орендодавець', right: 'Як орендар',
      vermieter: [
        { num: '1', title: 'Створити оголошення', desc: 'Сфотографуйте предмет і опублікуйте його за кілька хвилин.' },
        { num: '2', title: 'Прийняти запит', desc: 'Перегляньте запити на оренду та підтвердіть одним кліком.' },
        { num: '3', title: 'Заробити гроші', desc: 'Отримайте оплату безпечно через платформу після передачі.' },
      ],
      mieter: [
        { num: '1', title: 'Знайти предмет', desc: 'Знайдіть те, що вам потрібно — поблизу, за найкращою ціною.' },
        { num: '2', title: 'Забронювати і оплатити', desc: 'Оберіть дати та оплатіть безпечно через leendr.' },
        { num: '3', title: 'Забрати і повернути', desc: 'Використовуйте предмет і поверніть його у домовлений час.' },
      ],
    },
    categories: { label: 'Категорії', title: 'Все, що вам потрібно' },
    trust: {
      label: 'Безпека', title: 'Безпечно. Просто. Екологічно.',
      cards: [
        { icon: 'check', title: 'Верифіковані користувачі', desc: 'Перевірка особи для всіх користувачів платформи.' },
        { icon: 'lock', title: 'Безпечна оплата', desc: 'Ваші гроші звільняються лише після успішної передачі.' },
        { icon: 'globe', title: 'Для планети', desc: 'Ділитися замість купувати — це зберігає ресурси та зменшує CO\u2082.' },
      ],
    },
    city: { title: 'Leendr скоро запускається в Німеччині', subtitle: 'Будьте одним з перших у вашому місті' },
    waitlist: {
      title: 'Будьте з нами від початку', placeholder: 'Ваша електронна пошта',
      btnVermieter: 'Зареєструватися як орендодавець', btnMieter: 'Зареєструватися як орендар',
      fine: 'Безкоштовно \u00B7 Без картки \u00B7 Можна скасувати', success: 'Ви в списку!', exists: 'Ця пошта вже зареєстрована.',
    },
    footer: {
      desc: 'Маркетплейс P2P-оренди. Орендуй все, від усіх, будь-де.',
      platform: 'Платформа', browse: 'Переглянути', signup: 'Реєстрація', login: 'Увійти', how: 'Як це працює',
      legal: 'Правова інформація', imprint: 'Імпресум', privacy: 'Конфіденційність', terms: 'Умови', contact: 'Контакт',
      rights: 'Leendr GmbH',
    },
  },
  ar: {
    nav: { how: 'كيف يعمل', categories: 'الفئات', trust: 'الأمان', cta: 'سجّل الآن' },
    hero: {
      h1_1: 'استأجر أي شيء.', h1_2: 'من أي شخص.',
      subtitle: 'سوق التأجير من شخص لشخص — من الأدوات إلى الكاميرات، من الخيام إلى الدراجات.',
      btnRent: 'أعرض للإيجار', btnBorrow: 'استأجر شيئاً',
    },
    how: {
      label: 'كيف يعمل', title: 'منظوران. سوق واحد.',
      left: 'كمؤجّر', right: 'كمستأجر',
      vermieter: [
        { num: '1', title: 'أنشئ إعلاناً', desc: 'صوّر غرضك وانشره خلال دقائق.' },
        { num: '2', title: 'اقبل الطلبات', desc: 'راجع طلبات الإيجار ووافق بنقرة واحدة.' },
        { num: '3', title: 'اكسب المال', desc: 'احصل على الدفع بأمان عبر المنصة بعد التسليم.' },
      ],
      mieter: [
        { num: '1', title: 'ابحث عن غرض', desc: 'اعثر على ما تحتاجه — بالقرب منك، بأفضل سعر.' },
        { num: '2', title: 'احجز وادفع', desc: 'اختر تواريخك وادفع بأمان عبر leendr.' },
        { num: '3', title: 'استلم وأعد', desc: 'استخدم الغرض وأعده في الوقت المتفق عليه.' },
      ],
    },
    categories: { label: 'الفئات', title: 'كل ما تحتاجه' },
    trust: {
      label: 'الأمان', title: 'آمن. بسيط. مستدام.',
      cards: [
        { icon: 'check', title: 'مستخدمون موثّقون', desc: 'التحقق من الهوية لجميع المستخدمين على المنصة.' },
        { icon: 'lock', title: 'دفع آمن', desc: 'لا يتم تحرير أموالك إلا بعد التسليم الناجح.' },
        { icon: 'globe', title: 'من أجل البيئة', desc: 'المشاركة بدلاً من الشراء توفر الموارد وتقلل CO\u2082.' },
      ],
    },
    city: { title: 'leendr ينطلق قريباً في ألمانيا', subtitle: 'كن من الأوائل في مدينتك واحصل على وصول مبكر حصري' },
    waitlist: {
      title: 'كن معنا من البداية', placeholder: 'بريدك الإلكتروني',
      btnVermieter: 'سجّل كمؤجّر', btnMieter: 'سجّل كمستأجر',
      fine: 'مجاني \u00B7 بدون بطاقة \u00B7 إلغاء في أي وقت', success: 'أنت مسجّل!', exists: 'هذا البريد مسجّل بالفعل.',
    },
    footer: {
      desc: 'سوق التأجير من شخص لشخص. استأجر أي شيء، من أي شخص، في أي مكان.',
      platform: 'المنصة', browse: 'تصفّح', signup: 'تسجيل', login: 'تسجيل الدخول', how: 'كيف يعمل',
      legal: 'قانوني', imprint: 'البصمة', privacy: 'الخصوصية', terms: 'الشروط', contact: 'اتصل بنا',
      rights: 'Leendr GmbH',
    },
  },
};

const CATEGORIES = [
  { emoji: '\uD83D\uDD27', label: 'Werkzeug', labelEn: 'Tools', labelFr: 'Outils', labelEs: 'Herramientas', labelIt: 'Attrezzi', labelUk: 'Інструменти', labelAr: 'أدوات' },
  { emoji: '\uD83D\uDCF7', label: 'Elektronik', labelEn: 'Electronics', labelFr: '\u00C9lectronique', labelEs: 'Electr\u00F3nica', labelIt: 'Elettronica', labelUk: 'Електроніка', labelAr: 'إلكترونيات' },
  { emoji: '\u26FA', label: 'Outdoor & Sport', labelEn: 'Outdoor & Sports', labelFr: 'Outdoor & Sport', labelEs: 'Outdoor & Deporte', labelIt: 'Outdoor & Sport', labelUk: 'Спорт і відпочинок', labelAr: 'رياضة وتخييم' },
  { emoji: '\uD83D\uDEB2', label: 'Fahrzeuge', labelEn: 'Vehicles', labelFr: 'V\u00E9hicules', labelEs: 'Veh\u00EDculos', labelIt: 'Veicoli', labelUk: 'Транспорт', labelAr: 'مركبات' },
  { emoji: '\uD83C\uDFE0', label: 'Haushalt', labelEn: 'Household', labelFr: 'Maison', labelEs: 'Hogar', labelIt: 'Casa', labelUk: 'Побут', labelAr: 'منزل' },
  { emoji: '\uD83C\uDF89', label: 'Party & Events', labelEn: 'Party & Events', labelFr: 'F\u00EAtes', labelEs: 'Fiestas', labelIt: 'Feste', labelUk: 'Вечірки', labelAr: 'حفلات' },
  { emoji: '\uD83D\uDC57', label: 'Mode & Kost\u00FCme', labelEn: 'Fashion & Costumes', labelFr: 'Mode & Costumes', labelEs: 'Moda & Disfraces', labelIt: 'Moda & Costumi', labelUk: 'Мода і костюми', labelAr: 'أزياء' },
  { emoji: '\uD83C\uDF31', label: 'Garten', labelEn: 'Garden', labelFr: 'Jardin', labelEs: 'Jard\u00EDn', labelIt: 'Giardino', labelUk: 'Сад', labelAr: 'حديقة' },
];

const CITIES = ['Berlin', 'M\u00FCnchen', 'Hamburg', 'K\u00F6ln', 'Frankfurt'];

const TRUST_ICONS_MAP = { check: ShieldCheck, lock: Lock, globe: GlobeHemisphereWest };

const LeendrLogo = ({ size = 'md' }) => {
  const s = size === 'lg' ? { box: 44, word: 'text-2xl' } : { box: 34, word: 'text-xl' };
  return (
    <div className="flex items-center gap-2.5">
      <LeendrLogoMark size={s.box} />
      <span className={`${s.word} font-bold tracking-tight`} style={{ color: C.dark }}>leendr</span>
    </div>
  );
};

const getCatLabel = (cat, lang) => {
  const key = `label${lang.charAt(0).toUpperCase() + lang.slice(1)}`;
  return cat[key] || cat.label;
};

export default function LandingPage() {
  const { lang, switchLang } = useI18n();
  useTrackView('landing-hero');
  const t = T[lang] || T.de;
  const [email, setEmail] = useState('');
  const [waitlistStatus, setWaitlistStatus] = useState(null);
  const [trending, setTrending] = useState([]);
  const [trendingLocation, setTrendingLocation] = useState('');

  const LANGS = ['de', 'en', 'fr', 'es', 'it', 'uk', 'ar'];
  const LANG_LABELS = { de: 'DE', en: 'EN', fr: 'FR', es: 'ES', it: 'IT', uk: 'UK', ar: 'AR' };

  React.useEffect(() => {
    const params = new URLSearchParams();
    if (trendingLocation) params.set('location', trendingLocation);
    params.set('limit', '3');
    axios.get(`${API}/listings/trending?${params.toString()}`)
      .then(r => setTrending(r.data || []))
      .catch(() => setTrending([]));
  }, [trendingLocation]);

  // SEO structured data — Organization + WebSite + ItemList of cities for generative AI engines
  React.useEffect(() => {
    const origin = window.location.origin;
    const org = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Leendr',
      url: origin,
      logo: `${origin}/android-chrome-512x512.png`,
      description: 'Peer-to-peer rental marketplace for everyday items — tools, electronics, sports gear and more.',
      sameAs: ['https://leendr.de', 'https://leendr.com'],
      contactPoint: [{ '@type': 'ContactPoint', email: 'support@leendr.de', contactType: 'customer support', areaServed: 'DE' }],
    };
    const site = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Leendr',
      alternateName: 'leendr',
      url: origin,
      inLanguage: 'de-DE',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${origin}/listings?search={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    };
    const cities = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Leendr-Städte',
      itemListElement: [
        { slug: 'berlin', name: 'Berlin' }, { slug: 'muenchen', name: 'München' },
        { slug: 'hamburg', name: 'Hamburg' }, { slug: 'koeln', name: 'Köln' },
        { slug: 'frankfurt', name: 'Frankfurt' }, { slug: 'stuttgart', name: 'Stuttgart' },
        { slug: 'duesseldorf', name: 'Düsseldorf' }, { slug: 'leipzig', name: 'Leipzig' },
      ].map((c, i) => ({ '@type': 'ListItem', position: i + 1, name: c.name, url: `${origin}/miete/${c.slug}` })),
    };
    [['ld-org', org], ['ld-site', site], ['ld-cities', cities]].forEach(([id, data]) => {
      document.getElementById(id)?.remove();
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.id = id;
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
    });
    return () => ['ld-org', 'ld-site', 'ld-cities'].forEach(id => document.getElementById(id)?.remove());
  }, []);

  const handleWaitlist = async (role) => {
    if (!email.trim()) return;
    try {
      const { data } = await axios.post(`${API}/waitlist`, { email: email.trim(), role });
      setWaitlistStatus(data.status === 'exists' ? 'exists' : 'success');
      if (data.status !== 'exists') setEmail('');
      setTimeout(() => setWaitlistStatus(null), 5000);
    } catch { setWaitlistStatus('error'); }
  };

  return (
    <div style={{ fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif", color: C.text, scrollBehavior: 'smooth' }} dir={lang === 'ar' ? 'rtl' : 'ltr'}>

      {/* ─── Nav ─── */}
      <nav className="sticky top-0 z-50" style={{ backgroundColor: 'rgba(238,237,254,0.85)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}>
        <div className="max-w-6xl mx-auto px-5 md:px-8 flex items-center justify-between h-16">
          <Link to="/" data-testid="leendr-logo"><LeendrLogo /></Link>
          <div className="hidden md:flex items-center gap-8">
            <a href="#how" className="text-sm font-medium hover:opacity-70 transition-opacity" style={{ color: C.dark }}>{t.nav.how}</a>
            <a href="#categories" className="text-sm font-medium hover:opacity-70 transition-opacity" style={{ color: C.dark }}>{t.nav.categories}</a>
            <a href="#trust" className="text-sm font-medium hover:opacity-70 transition-opacity" style={{ color: C.dark }}>{t.nav.trust}</a>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center rounded-full overflow-hidden" style={{ border: `0.5px solid ${C.borderLight}` }} data-testid="lang-selector">
              {LANGS.map(l => (
                <button key={l} onClick={() => switchLang(l)} data-testid={`lang-${l}`}
                  className="px-2 py-1.5 text-[11px] font-bold tracking-wide transition-all"
                  style={{ backgroundColor: lang === l ? C.primary : 'transparent', color: lang === l ? C.white : C.accent }}>
                  {LANG_LABELS[l]}
                </button>
              ))}
            </div>
            <Link to="/anmelden" data-testid="nav-cta"
              onClick={() => trackCta('nav-cta')}
              className="text-sm font-semibold px-5 py-2 rounded-full transition-opacity hover:opacity-90"
              style={{ backgroundColor: C.primary, color: C.white }}>
              {t.nav.cta}
            </Link>
          </div>
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section style={{ backgroundColor: C.bg, position: 'relative', overflow: 'hidden' }}>
        {/* Subtle geometric pattern */}
        <div style={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #534AB7 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <div className="max-w-6xl mx-auto px-5 md:px-8 pt-16 pb-20 md:pt-24 md:pb-32 relative">
          <motion.div className="max-w-2xl mx-auto text-center" initial="hidden" animate="visible" variants={fadeUp}>
            <motion.div variants={fadeUp} custom={-1} className="flex justify-center mb-8" data-testid="hero-logo">
              <div className="leendr-hero-logo">
                <LeendrLogoMark size={120} arrowScale={0.6} />
              </div>
            </motion.div>
            <motion.h1 variants={fadeUp} custom={0} className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-[1.08] tracking-tight mb-5" style={{ color: C.dark }}>
              {t.hero.h1_1}<br />
              <span style={{ color: C.primary }}>{t.hero.h1_2}</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={1} className="text-base md:text-lg leading-relaxed mb-10 max-w-lg mx-auto" style={{ color: '#6B6B6B' }}>
              {t.hero.subtitle}
            </motion.p>
            <motion.div variants={fadeUp} custom={2} className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/create-listing" data-testid="hero-btn-rent"
                onClick={() => trackCta('hero-btn-rent')}
                className="px-7 py-3.5 rounded-full text-sm font-semibold transition-all hover:opacity-90 inline-flex items-center justify-center gap-2"
                style={{ backgroundColor: C.primary, color: C.white }}>
                <Tag size={18} weight="bold" /> {t.hero.btnRent}
              </Link>
              <Link to="/listings" data-testid="hero-btn-borrow"
                onClick={() => trackCta('hero-btn-borrow')}
                className="px-7 py-3.5 rounded-full text-sm font-semibold transition-all hover:opacity-90 inline-flex items-center justify-center gap-2"
                style={{ border: `2px solid ${C.primary}`, color: C.primary, backgroundColor: 'transparent' }}>
                {t.hero.btnBorrow} <ArrowRight size={16} weight="bold" />
              </Link>
            </motion.div>
            <motion.p variants={fadeUp} custom={3} className="mt-6 text-sm" style={{ color: '#6B6B6B' }} data-testid="hero-passwordless-hint">
              ✨ <Link to="/anmelden" onClick={() => trackCta('hero-passwordless-hint')} className="underline hover:opacity-70" style={{ color: C.primary, fontWeight: 600 }}>Anmelden in 30 Sekunden</Link> — ohne Passwort, mit Code per E-Mail
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ─── How It Works — 2 columns ─── */}
      <section id="how" className="py-20 md:py-28" style={{ backgroundColor: C.white }}>
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
            <motion.p variants={fadeUp} custom={0} className="text-xs font-bold tracking-[0.2em] uppercase mb-3" style={{ color: C.accent }}>{t.how.label}</motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ color: C.dark }}>{t.how.title}</motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Vermieter */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={fadeUp} custom={0}
              className="p-8 rounded-xl" style={{ backgroundColor: C.bg, border: `0.5px solid ${C.border}`, borderRadius: 12 }}>
              <div className="flex items-center gap-2 mb-6">
                <UserCirclePlus size={22} weight="duotone" style={{ color: C.primary }} />
                <h3 className="text-lg font-bold" style={{ color: C.dark }}>{t.how.left}</h3>
              </div>
              <div className="space-y-5">
                {t.how.vermieter.map(s => (
                  <div key={s.num} className="flex gap-4">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: C.primary }}>
                      <span className="text-sm font-bold" style={{ color: C.white }}>{s.num}</span>
                    </div>
                    <div>
                      <p className="text-sm font-bold" style={{ color: C.dark }}>{s.title}</p>
                      <p className="text-sm mt-0.5" style={{ color: '#6B6B6B' }}>{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            {/* Mieter */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={fadeUp} custom={1}
              className="p-8 rounded-xl" style={{ border: `0.5px solid ${C.border}`, borderRadius: 12 }}>
              <div className="flex items-center gap-2 mb-6">
                <Tag size={22} weight="duotone" style={{ color: C.primary }} />
                <h3 className="text-lg font-bold" style={{ color: C.dark }}>{t.how.right}</h3>
              </div>
              <div className="space-y-5">
                {t.how.mieter.map(s => (
                  <div key={s.num} className="flex gap-4">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: C.primary }}>
                      <span className="text-sm font-bold" style={{ color: C.white }}>{s.num}</span>
                    </div>
                    <div>
                      <p className="text-sm font-bold" style={{ color: C.dark }}>{s.title}</p>
                      <p className="text-sm mt-0.5" style={{ color: '#6B6B6B' }}>{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── Trending in deiner Region ─── */}
      {trending.length > 0 && (
        <section id="trending" className="py-16 md:py-20" style={{ backgroundColor: C.white }}>
          <div className="max-w-6xl mx-auto px-5 md:px-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
              <div>
                <p className="text-xs font-bold tracking-[0.2em] uppercase mb-3" style={{ color: C.accent }}>
                  {lang === 'de' ? 'Trending' : lang === 'fr' ? 'Tendance' : lang === 'es' ? 'Tendencia' : lang === 'it' ? 'Di tendenza' : lang === 'uk' ? 'У тренді' : lang === 'ar' ? 'الأكثر رواجاً' : 'Trending'}
                </p>
                <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ color: C.dark }}>
                  {trendingLocation
                    ? (lang === 'de' ? `In ${trendingLocation} gerade gefragt` : `Hot in ${trendingLocation}`)
                    : (lang === 'de' ? 'Diese Woche besonders gefragt' : 'Most popular this week')}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <input
                  data-testid="trending-location-input"
                  value={trendingLocation}
                  onChange={(e) => setTrendingLocation(e.target.value)}
                  placeholder={lang === 'de' ? 'Stadt oder PLZ' : 'City or ZIP'}
                  className="h-10 px-3 text-sm rounded-md outline-none transition-colors focus:border-[#534AB7]"
                  style={{ border: `1px solid ${C.accent}`, backgroundColor: C.white, color: C.dark, minWidth: 200 }}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="trending-grid">
              {trending.map((tl, i) => (
                <Link
                  key={tl.id}
                  to={`/listings/${tl.id}`}
                  data-testid={`trending-card-${tl.id}`}
                  className="group block overflow-hidden transition-transform hover:-translate-y-1"
                  style={{ backgroundColor: C.white, border: `0.5px solid ${C.accent}`, borderRadius: 16 }}
                >
                  <div className="aspect-[4/3] overflow-hidden relative" style={{ backgroundColor: C.bg }}>
                    <TrendingThumb listing={tl} />
                    <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full" style={{ backgroundColor: C.dark, color: C.white }}>
                      #{i + 1} · {tl.trending_score}
                    </span>
                  </div>
                  <div className="p-5">
                    <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-1" style={{ color: C.accent }}>{tl.category}</p>
                    <h3 className="text-base font-bold leading-snug mb-2 truncate" style={{ color: C.dark }}>{tl.title}</h3>
                    <div className="flex items-center justify-between">
                      <span className="text-sm" style={{ color: '#6A6A6A' }}>{tl.location || '—'}</span>
                      <span className="text-base font-bold" style={{ color: C.primary }}>{tl.price_per_day?.toFixed(2)} €/T</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── Categories — 8 visual cards ─── */}
      <section id="categories" className="py-16 md:py-20" style={{ backgroundColor: C.bg }}>
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
            <motion.p variants={fadeUp} custom={0} className="text-xs font-bold tracking-[0.2em] uppercase mb-3" style={{ color: C.accent }}>{t.categories.label}</motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ color: C.dark }}>{t.categories.title}</motion.h2>
          </motion.div>
          <motion.div className="grid grid-cols-2 sm:grid-cols-4 gap-4" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
            {CATEGORIES.map((cat, i) => (
              <motion.div key={i} variants={fadeUp} custom={i * 0.3}
                className="flex flex-col items-center gap-3 p-6 cursor-pointer transition-all hover:border-[#534AB7] group"
                style={{ backgroundColor: C.white, border: `0.5px solid ${C.accent}`, borderRadius: 12 }}
                data-testid={`category-${i}`}>
                <span className="text-[32px] group-hover:scale-110 transition-transform">{cat.emoji}</span>
                <span className="text-sm font-semibold text-center" style={{ color: C.dark }}>{getCatLabel(cat, lang)}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── Trust & Safety ─── */}
      <section id="trust" className="py-20 md:py-28" style={{ backgroundColor: C.white }}>
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
            <motion.p variants={fadeUp} custom={0} className="text-xs font-bold tracking-[0.2em] uppercase mb-3" style={{ color: C.accent }}>{t.trust.label}</motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ color: C.dark }}>{t.trust.title}</motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.trust.cards.map((card, i) => {
              const Icon = TRUST_ICONS_MAP[card.icon];
              return (
                <motion.div key={i} className="p-8" style={{ border: `0.5px solid ${C.border}`, borderRadius: 12 }}
                  initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fadeUp} custom={i}>
                  <div className="w-12 h-12 flex items-center justify-center mb-5" style={{ backgroundColor: C.bg, borderRadius: 12 }}>
                    <Icon size={24} weight="duotone" style={{ color: C.primary }} />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{ color: C.dark }}>{card.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#6B6B6B' }}>{card.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── City Launch ─── */}
      <section className="py-20 md:py-24" style={{ backgroundColor: C.dark }}>
        <motion.div className="max-w-3xl mx-auto px-5 md:px-8 text-center" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
          <motion.h2 variants={fadeUp} custom={0} className="text-3xl md:text-4xl font-extrabold tracking-tight mb-4" style={{ color: C.white }}>
            {t.city.title}
          </motion.h2>
          <motion.p variants={fadeUp} custom={1} className="text-base mb-10" style={{ color: C.accent }}>
            {t.city.subtitle}
          </motion.p>
          <motion.div variants={fadeUp} custom={2} className="flex flex-wrap justify-center gap-3">
            {CITIES.map(city => (
              <span key={city} className="px-5 py-2 rounded-full text-sm font-semibold" style={{ backgroundColor: C.primary, color: C.white }}>
                {city}
              </span>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* ─── Waitlist ─── */}
      <section id="waitlist" className="py-20 md:py-28" style={{ backgroundColor: C.bg }}>
        <motion.div className="max-w-md mx-auto px-5 md:px-8 text-center" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
          <motion.h2 variants={fadeUp} custom={0} className="text-3xl md:text-4xl font-extrabold tracking-tight mb-8" style={{ color: C.dark }}>
            {t.waitlist.title}
          </motion.h2>
          <motion.div variants={fadeUp} custom={1} className="space-y-3">
            <input
              data-testid="waitlist-email"
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder={t.waitlist.placeholder}
              className="w-full px-5 py-3.5 rounded-full text-sm outline-none transition-all focus:ring-2"
              style={{ backgroundColor: C.white, border: `0.5px solid ${C.border}`, color: C.text, '--tw-ring-color': C.accent }}
            />
            <div className="flex flex-col sm:flex-row gap-3">
              <button data-testid="waitlist-vermieter" onClick={() => handleWaitlist('vermieter')} disabled={!email.trim()}
                className="flex-1 px-5 py-3 rounded-full text-sm font-semibold transition-all hover:opacity-90 disabled:opacity-40"
                style={{ backgroundColor: C.primary, color: C.white }}>
                {t.waitlist.btnVermieter}
              </button>
              <button data-testid="waitlist-mieter" onClick={() => handleWaitlist('mieter')} disabled={!email.trim()}
                className="flex-1 px-5 py-3 rounded-full text-sm font-semibold transition-all hover:opacity-90 disabled:opacity-40"
                style={{ border: `2px solid ${C.primary}`, color: C.primary, backgroundColor: 'transparent' }}>
                {t.waitlist.btnMieter}
              </button>
            </div>
            {waitlistStatus && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-sm font-medium" style={{ color: waitlistStatus === 'exists' ? '#B45309' : C.primary }}>
                {waitlistStatus === 'exists' ? t.waitlist.exists : t.waitlist.success}
              </motion.p>
            )}
            <p className="text-xs pt-1" style={{ color: '#999' }}>{t.waitlist.fine}</p>
          </motion.div>
        </motion.div>
      </section>

      {/* ─── Footer ─── */}
      <footer style={{ backgroundColor: C.white, borderTop: `0.5px solid ${C.border}` }}>
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-1">
              <LeendrLogo />
              <p className="text-sm mt-3 max-w-xs" style={{ color: '#6B6B6B' }}>{t.footer.desc}</p>
            </div>
            <div>
              <p className="text-xs font-bold tracking-[0.15em] uppercase mb-3" style={{ color: C.accent }}>{t.footer.platform}</p>
              <div className="space-y-2">
                <Link to="/listings" className="block text-sm hover:opacity-70 transition-opacity" style={{ color: C.dark }} data-testid="footer-browse">{t.footer.browse}</Link>
                <Link to="/anmelden" className="block text-sm hover:opacity-70 transition-opacity" style={{ color: C.dark }} data-testid="footer-signup">{t.footer.signup}</Link>
                <Link to="/anmelden" className="block text-sm hover:opacity-70 transition-opacity" style={{ color: C.dark }} data-testid="footer-login">{t.footer.login}</Link>
                <a href="#how" className="block text-sm hover:opacity-70 transition-opacity" style={{ color: C.dark }}>{t.footer.how}</a>
              </div>
            </div>
            <div>
              <p className="text-xs font-bold tracking-[0.15em] uppercase mb-3" style={{ color: C.accent }}>{t.footer.legal}</p>
              <div className="space-y-2">
                <Link to="/impressum" className="block text-sm hover:opacity-70 transition-opacity" style={{ color: C.dark }} data-testid="footer-imprint">{t.footer.imprint}</Link>
                <Link to="/datenschutz" className="block text-sm hover:opacity-70 transition-opacity" style={{ color: C.dark }} data-testid="footer-privacy">{t.footer.privacy}</Link>
                <Link to="/agb" className="block text-sm hover:opacity-70 transition-opacity" style={{ color: C.dark }} data-testid="footer-terms">{t.footer.terms}</Link>
                <Link to="/faq" className="block text-sm hover:opacity-70 transition-opacity" style={{ color: C.dark }} data-testid="footer-faq">FAQ</Link>
                <Link to="/miete" className="block text-sm hover:opacity-70 transition-opacity" style={{ color: C.dark }} data-testid="footer-cities">Städte</Link>
              </div>
            </div>
            <div>
              <p className="text-xs font-bold tracking-[0.15em] uppercase mb-3" style={{ color: C.accent }}>Social</p>
              <div className="space-y-2">
                <a href="https://leendr.com" className="block text-sm hover:opacity-70 transition-opacity" style={{ color: C.dark }}>leendr.com</a>
                <a href="https://leendr.de" className="block text-sm hover:opacity-70 transition-opacity" style={{ color: C.dark }}>leendr.de</a>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between mt-12 pt-8 gap-4" style={{ borderTop: `0.5px solid ${C.border}` }}>
            <p className="text-xs" style={{ color: '#6B6B6B' }}>&copy; 2025 {t.footer.rights} &middot; leendr.com &middot; leendr.de</p>
            <div className="flex items-center gap-4">
              <a href="#" className="hover:opacity-70 transition-opacity" style={{ color: C.dark }} aria-label="Instagram"><InstagramLogo size={20} /></a>
              <a href="#" className="hover:opacity-70 transition-opacity" style={{ color: C.dark }} aria-label="TikTok">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15.2a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.73a8.19 8.19 0 0 0 4.76 1.52V6.8a4.84 4.84 0 0 1-1-.11z"/></svg>
              </a>
              <a href="#" className="hover:opacity-70 transition-opacity" style={{ color: C.dark }} aria-label="LinkedIn"><LinkedinLogo size={20} /></a>
              <a href="#" className="hover:opacity-70 transition-opacity" style={{ color: C.dark }} aria-label="X"><TwitterLogo size={20} /></a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function TrendingThumb({ listing }) {
  const [url, setUrl] = React.useState(null);
  React.useEffect(() => {
    const path = (listing.images || [])[0];
    if (!path) { setUrl(null); return; }
    if (path.startsWith('http')) { setUrl(path); return; }
    axios.get(`${API}/files/${path}`, { responseType: 'blob' })
      .then(res => setUrl(URL.createObjectURL(res.data)))
      .catch(() => setUrl(null));
  }, [listing.images]);
  if (!url) return <div className="w-full h-full flex items-center justify-center text-[#CCCCCC] text-sm">—</div>;
  return <img src={url} alt={listing.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />;
}
